import { useState, useRef, useEffect } from "react";
import useShadowRef from "./useShadowRef";

/**
 * Custom hook for tracking scroll events and viewport settings
 * Used by both web reader and extension reader to eliminate duplication
 * Contains all viewport calculation logic internally
 * 
 * @param {Object} config - Configuration object
 * @param {Object} config.api - The Zeeguu API instance
 * @param {Object} config.articleInfo - Article information including source_id
 * @param {Function} config.getReadingSessionId - Function to get current reading session ID
 * @param {number} config.sessionDuration - Current session duration in seconds
 * @param {string} config.scrollHolderId - ID of the scroll container element (default: "scrollHolder")
 * @param {string} config.bottomRowId - ID of the bottom row element (default: "bottomRow")
 * @param {number} config.sampleFrequency - How often to sample scroll events in seconds (default: 1)
 * @param {string} config.source - Source identifier (e.g., "UMR" for web, "EXTENSION_SOURCE" for extension)
 * 
 * @returns {Object} - Object containing:
 *   - scrollPosition: Current scroll position ratio (0-1)
 *   - viewPortSettings: JSON string of viewport configuration
 *   - handleScroll: Function to call on scroll events
 *   - sendFinalScrollEvent: Function to send final scroll event on unmount
 *   - uploadScrollActivity: Function to upload periodic scroll activity
 *   - scrollEvents: Ref to current scroll events array
 */
export default function useScrollTracking({
  api,
  articleInfo,
  getReadingSessionId,
  sessionDuration,
  scrollHolderId = "scrollHolder",
  bottomRowId = "bottomRow",
  sampleFrequency = 1,
  source = "UMR",
}) {
  const [scrollPosition, setScrollPosition] = useState(0);
  const [viewPortSettings, setViewPortSettings] = useState("");

  const scrollEvents = useRef([]);
  const lastSampleTimer = useRef(0);

  // Use shadow refs for values that change frequently
  const viewPortSettingsRef = useShadowRef(viewPortSettings);
  const sessionDurationRef = useShadowRef(sessionDuration);
  const articleInfoRef = useShadowRef(articleInfo);

  /**
   * Calculate the scroll ratio (0-1) representing how far down the page we are
   */
  const getScrollRatio = () => {
    const scrollElement = document.getElementById(scrollHolderId);
    if (!scrollElement) return 0;
    
    const scrollY = scrollElement.scrollTop;
    const bottomRowElement = document.getElementById(bottomRowId);
    const bottomRowHeight = bottomRowElement ? bottomRowElement.offsetHeight : 0;
    
    const endArticle = scrollElement.scrollHeight - scrollElement.clientHeight - bottomRowHeight;
    
    // Avoid division by zero
    if (endArticle <= 0) return 0;
    
    return Math.min(1, Math.max(0, scrollY / endArticle));
  };

  /**
   * Calculate and update viewport settings (viewport ratio for tolerance calculation)
   */
  const updateViewPortSettings = () => {
    const scrollElement = document.getElementById(scrollHolderId);
    if (!scrollElement) return;
    
    const bottomRowElement = document.getElementById(bottomRowId);
    const bottomHeight = bottomRowElement ? bottomRowElement.offsetHeight : 0;
    
    // Calculate viewport ratio for tolerance calculation
    // This represents what fraction of the article is visible on screen
    const viewportRatio = scrollElement.clientHeight / 
      (scrollElement.scrollHeight - bottomHeight);
    
    // Store just viewport ratio (optimized format)
    setViewPortSettings(
      JSON.stringify({
        viewportRatio: Math.max(0, Math.min(1, viewportRatio)) // Clamp between 0 and 1
      })
    );
  };

  /**
   * Handle scroll events - track position and sample at specified frequency.
   * Kept lean: only the cheap ratio read + setState runs on every scroll.
   * Viewport settings are recomputed on mount and resize (see below), not
   * per scroll — viewportRatio depends on clientHeight / scrollHeight, both
   * stable during a scroll, and the per-scroll JSON.stringify was making
   * the progress bar feel like it was lagging behind the scroll.
   */
  const handleScroll = () => {
    const ratio = getScrollRatio();
    setScrollPosition(ratio);

    const currentTimer = sessionDurationRef.current;
    if (currentTimer - lastSampleTimer.current >= sampleFrequency) {
      scrollEvents.current.push([currentTimer, Math.floor(ratio * 100)]);
      lastSampleTimer.current = currentTimer;
    }
  };

  /**
   * Upload periodic scroll activity to the server
   */
  const uploadScrollActivity = () => {
    const readingSessionId = getReadingSessionId?.();
    if (!readingSessionId || !articleInfoRef.current) return;
    
    // Update reading session duration
    if (api.readingSessionUpdate) {
      api.readingSessionUpdate(readingSessionId, sessionDurationRef.current);
    }
    
    // Send periodic SCROLL events with accumulated scroll data
    if (scrollEvents.current && scrollEvents.current.length > 0 && viewPortSettingsRef.current) {
      console.log('📊 Sending periodic SCROLL event:', {
        articleID: articleInfoRef.current.id,
        scrollEventsCount: scrollEvents.current.length,
        scrollData: scrollEvents.current.slice(0, 5), // Show first 5 entries for debugging
        viewportSettings: JSON.parse(viewPortSettingsRef.current || '{}') // Show the viewport settings
      });
      
      api.logUserActivity(
        api.SCROLL,
        articleInfoRef.current.id,
        viewPortSettingsRef.current, // Optimized viewport settings
        JSON.stringify(scrollEvents.current).slice(0, 4096), // Limit data size
        articleInfoRef.current.source_id
      );
      
      // Don't clear events - keep them for the final scroll event
    }
  };

  /**
   * Send final scroll event (typically called on component unmount)
   */
  const sendFinalScrollEvent = () => {
    if (!articleInfoRef.current || !viewPortSettingsRef.current) return;
    
    console.log('🏁 Sending final SCROLL event:', {
      articleID: articleInfoRef.current.id,
      scrollEventsCount: scrollEvents.current.length,
      scrollData: scrollEvents.current.slice(0, 5), // Show first 5 entries for debugging
      viewportSettings: JSON.parse(viewPortSettingsRef.current || '{}') // Show the viewport settings
    });
    
    api.logUserActivity(
      api.SCROLL,
      articleInfoRef.current.id,
      viewPortSettingsRef.current,
      JSON.stringify(scrollEvents.current).slice(0, 4096),
      articleInfoRef.current.source_id
    );
  };

  // Viewport settings on mount + window resize. Recomputing per scroll
  // burned a JSON.stringify each frame for no gain — these only change
  // when the layout does.
  useEffect(() => {
    updateViewPortSettings();
    window.addEventListener("resize", updateViewPortSettings);
    return () => window.removeEventListener("resize", updateViewPortSettings);
  }, []);

  /**
   * Initialize scroll tracking (call this in onCreate or useEffect)
   */
  const initializeScrollTracking = () => {
    scrollEvents.current = [];
    lastSampleTimer.current = 0;
    setScrollPosition(0);
    updateViewPortSettings();
  };

  return {
    scrollPosition,
    viewPortSettings,
    handleScroll,
    sendFinalScrollEvent,
    uploadScrollActivity,
    initializeScrollTracking,
    scrollEvents,
  };
}